import React, { Component } from "react";
import { LuImage, LuFileText, LuPaperclip } from "react-icons/lu";

// Shared rendering for thread "file" content blocks (attachments uploaded to or produced by a
// session thread) plus the auth-aware cache that turns an alias into a browser-usable object URL.
//
// SECURITY: bytes come back from sessionFileDownload as an ArrayBuffer with no inherent MIME type.
// content_type is client-supplied, so a Blob is only ever typed with the block's declared
// content_type when that type is one of the exact raster types below — every other blob (including
// "image/svg+xml") is wrapped as "application/octet-stream". A broader "starts with image/" match
// would let a block claim "image/svg+xml": ThreadImage's full-size view opens the object URL as a
// top-level navigation (window.open), and an SVG opened that way renders as a standalone document
// whose embedded <script> executes under the blob: URL's inherited page origin — a stored-XSS path.
// Restricting to the exact raster set keeps that payload undecodable, so it falls to FileChip's
// octet-stream <a download> instead: images render via <img>/window.open, everything else only ever
// leaves as a download.
const RASTER_IMAGE_TYPES = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

// Cache: module-level Map keyed by `${revisionId}/${sessionId}/${alias}` holding either an in-flight
// promise (dedupes concurrent resolvers) or the resolved object URL. Otherwise it lives for the tab's
// life — the same trade-off as browser HTTP caching for session-scoped assets — but api.js's
// clearIdentityCaches() calls clearFileCache() below on every identity switch (startActing/
// stopActing/unAuthenticate), so a cached blob: URL (and the authorization check it stands in for)
// never survives past the identity it was resolved for.
const fileCache = new Map();

function cacheKey(revisionId, sessionId, alias) {
  return `${revisionId}/${sessionId}/${alias}`;
}

function isRasterImageType(contentType) {
  return RASTER_IMAGE_TYPES.has(contentType);
}

export function isImageBlock(block) {
  return isRasterImageType(block?.file?.content_type);
}

function humanSize(bytes) {
  if ( bytes < 1024 ) return `${bytes} B`;
  if ( bytes < 1024 * 1024 ) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function iconForType(contentType) {
  if ( typeof contentType === "string" ) {
    if ( contentType.startsWith("image/") ) return LuImage;
    if ( contentType.startsWith("text/") || contentType === "application/pdf" || contentType.includes("document") ) {
      return LuFileText;
    }
  }
  return LuPaperclip;
}

export function resolveFileUrl(api, revisionId, sessionId, block) {
  const alias = block.file.alias;
  const key = cacheKey(revisionId, sessionId, alias);
  const cached = fileCache.get(key);
  if ( cached ) return cached.url ? Promise.resolve(cached.url) : cached.promise;

  const promise = api.sessionFileDownload(revisionId, sessionId, alias).then((response) => {
    const type = isImageBlock(block) ? block.file.content_type : "application/octet-stream";
    const url = URL.createObjectURL(new Blob([response.binary], { type }));
    fileCache.set(key, { url });
    return url;
  }, (error) => {
    fileCache.delete(key);
    throw error;
  });
  fileCache.set(key, { promise });
  return promise;
}

export function seedFileUrl(revisionId, sessionId, alias, fileOrBlob) {
  const key = cacheKey(revisionId, sessionId, alias);
  const type = isRasterImageType(fileOrBlob.type) ? fileOrBlob.type : "application/octet-stream";
  const url = URL.createObjectURL(new Blob([fileOrBlob], { type }));
  fileCache.set(key, { url });
  return url;
}

export function clearFileCacheForTests() {
  fileCache.clear();
}

// Revokes every resolved object URL before dropping it. Called from api.js's clearIdentityCaches()
// on every identity switch — an in-flight (not yet resolved) entry has no url to revoke, so it is
// simply dropped along with everything else.
export function clearFileCache() {
  for ( const entry of fileCache.values() ) {
    if ( entry && entry.url ) URL.revokeObjectURL(entry.url);
  }
  fileCache.clear();
}

// Hidden-anchor download, mirroring graphs/dnn/shell/index.js's completeModelComponentDownload.
function downloadUrl(url, name) {
  const a = document.createElement("a");
  a.style.display = "none";
  document.body.appendChild(a);
  a.href = url;
  a.setAttribute("download", name);
  a.click();
  document.body.removeChild(a);
}

export class ThreadImage extends Component {
  constructor(props) {
    super(props);
    this.state = { url: null, failed: false };
  }

  componentDidMount() {
    this.mounted = true;
    const { fileContext, block } = this.props;
    // fileContext is documented optional (threadItems.js: "omit and they still render, but never
    // fetch") — degrade to the same context-free FileChip render() already falls back to on a fetch
    // failure, rather than reading fileContext.api of undefined.
    if ( !fileContext ) {
      this.setState({ failed: true });
      return;
    }
    resolveFileUrl(fileContext.api, fileContext.revisionId, fileContext.sessionId, block).then((url) => {
      if ( this.mounted ) this.setState({ url });
    }, () => {
      if ( this.mounted ) this.setState({ failed: true });
    });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  handleError = () => {
    this.setState({ failed: true });
  };

  handleClick = () => {
    window.open(this.state.url, "_blank", "noopener");
  };

  render() {
    const { fileContext, block } = this.props;
    const { url, failed } = this.state;
    if ( failed ) return <FileChip fileContext={fileContext} block={block} />;
    if ( !url ) return <div className="bh-thread-image is-loading" />;
    return (
      <img
        className="bh-thread-image"
        src={url}
        alt={block.file.name}
        onError={this.handleError}
        onClick={this.handleClick}
      />
    );
  }
}

export class FileChip extends Component {
  constructor(props) {
    super(props);
    this.state = { url: null, failed: false };
  }

  componentDidMount() {
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  // Clicking is the only trigger for fetching bytes — a chip that pre-warmed the cache on mount
  // would download every attachment in a thread whether or not the user ever opened one.
  handleClick = () => {
    const { fileContext, block } = this.props;
    if ( !fileContext ) return;
    resolveFileUrl(fileContext.api, fileContext.revisionId, fileContext.sessionId, block).then((url) => {
      if ( this.mounted ) this.setState({ url });
      downloadUrl(url, block.file.name);
    }, () => {
      if ( this.mounted ) this.setState({ failed: true });
    });
  };

  render() {
    const { fileContext, block } = this.props;
    const { failed } = this.state;
    const { name, size, content_type: contentType } = block.file;
    const Icon = iconForType(contentType);
    const classes = `bh-file-chip${failed ? " is-failed" : ""}`;
    return (
      <button type="button" className={classes} disabled={!fileContext} onClick={this.handleClick}>
        <Icon />
        <span className="bh-file-chip-name">{name}</span>
        <span className="bh-file-chip-size">{humanSize(size)}</span>
      </button>
    );
  }
}

export class UnsupportedBlock extends Component {
  render() {
    const { type } = this.props;
    return <span className="bh-unsupported-block">unsupported block: {type}</span>;
  }
}
